import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
} from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import {
  useOtaUpdates,
  usePromptNativeBuildUpdates,
} from '@/features/appUpdate';
import { NotificationHandlers } from '@/features/notifications/components/notificationHandlers';
import { BodyBackgroundHandler } from '@/shared/components/bodyBackgroundHandler';
import { DeepLinkHandler } from '@/shared/components/deepLinkHandler';
import { NavigationProgress } from '@/shared/components/navigationProgress';
import { Toaster } from '@/shared/components/ui/toaster';
import { VersionCheckHandler } from '@/shared/components/versionCheckHandler';
import { useSplashScreen } from '@/shared/hooks/useSplashScreen';
import { isNativePlatform } from '@/shared/utils/platform';

// Lazy load devtools - will be completely excluded in production due to import.meta.env.DEV check
const DevTools = lazy(() =>
  import('@/shared/components/devTools').then(mod => ({
    default: mod.DevTools,
  })),
);

interface MyRouterContext {
  queryClient: QueryClient;
  bodyBackground?: string;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'Gankster Duos — Find Your People In Game',
      },
    ],
  }),
});

function RouteComponent() {
  useSplashScreen();
  useOtaUpdates();
  usePromptNativeBuildUpdates();

  return (
    <>
      <BodyBackgroundHandler />
      <NavigationProgress />
      <HeadContent />
      <div className="relative flex h-full overflow-y-auto">
        <Outlet />
      </div>
      <DeepLinkHandler />
      <NotificationHandlers />
      <VersionCheckHandler />
      {import.meta.env.DEV && !isNativePlatform() && (
        <Suspense fallback={null}>
          <DevTools />
        </Suspense>
      )}
      <Toaster />
    </>
  );
}
